import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Users, CheckSquare, DollarSign, Target, TrendingUp, Clock,
  ChevronRight, FileText, AlertTriangle, Calendar, Receipt, CreditCard
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Dashboard = ({ stats, contacts, tasks, deals, invoices }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value || 0);
  };

  const openTasks = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
  const recentContacts = contacts.slice(0, 5);
  const overdueInvoices = invoices.filter(inv => inv.status === 'overdue');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Dashboard</h2>
          <p className="text-slate-500 font-manrope">Overview of your CRM performance</p>
        </div>
        <div className="flex gap-2">
          <a href="/store" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">
            <Target size={16} className="text-royal-blue-600" /> Storefront
          </a>
          <a href={process.env.REACT_APP_BOOKING_URL || ""} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">
            <Calendar size={16} className="text-green-600" /> Booking Page
          </a>
        </div>
      </div>

      {/* Overdue Invoices Alert */}
      {overdueInvoices.length > 0 && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            <span className="font-semibold">{overdueInvoices.length} overdue invoice{overdueInvoices.length > 1 ? 's' : ''}</span> totaling {formatCurrency(stats.overdue_amount)} require attention.
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-hover border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-manrope">Total Pipeline</p>
                <p className="font-cabinet text-3xl font-bold text-slate-900">{formatCurrency(stats.total_pipeline_value)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-gold-50 flex items-center justify-center">
                <DollarSign className="text-gold-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-manrope">Total Revenue</p>
                <p className="font-cabinet text-3xl font-bold text-green-600">{formatCurrency(stats.total_revenue)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                <CreditCard className="text-green-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-manrope">Pending Payments</p>
                <p className="font-cabinet text-3xl font-bold text-orange-600">{formatCurrency(stats.pending_payments)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                <Receipt className="text-orange-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-200 border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-manrope">Overdue</p>
                <p className="font-cabinet text-3xl font-bold text-red-600">{formatCurrency(stats.overdue_amount)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second Row Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-hover border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-manrope">Total Contacts</p>
                <p className="font-cabinet text-2xl font-bold text-slate-900">{stats.total_contacts}</p>
              </div>
              <Users className="text-royal-blue-600" size={20} />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-manrope">Open Tasks</p>
                <p className="font-cabinet text-2xl font-bold text-slate-900">{stats.open_tasks}</p>
              </div>
              <CheckSquare className="text-purple-600" size={20} />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-manrope">Total Invoices</p>
                <p className="font-cabinet text-2xl font-bold text-slate-900">{stats.total_invoices}</p>
              </div>
              <Receipt className="text-gold-600" size={20} />
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 font-manrope">Deals Won</p>
                <p className="font-cabinet text-2xl font-bold text-slate-900">{stats.deals_won}</p>
              </div>
              <Target className="text-green-600" size={20} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Overdue Invoices */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-cabinet text-lg flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={18} />
              Overdue Invoices
            </CardTitle>
            <CardDescription>Invoices requiring immediate attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {overdueInvoices.slice(0, 5).map((invoice) => {
                const contact = contacts.find(c => c.id === invoice.contact_id);
                return (
                  <div key={invoice.id} className="flex items-center gap-3 p-3 rounded-lg bg-red-50 hover:bg-red-100 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                      <p className="text-sm text-slate-500 truncate">{contact?.name || 'Unknown'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-red-600">{formatCurrency(invoice.total)}</p>
                      <p className="text-xs text-slate-500">Due: {new Date(invoice.due_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                );
              })}
              {overdueInvoices.length === 0 && (
                <p className="text-center text-slate-500 py-4">No overdue invoices</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-cabinet text-lg">Recent Contacts</CardTitle>
            <CardDescription>Latest contacts added to your CRM</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentContacts.map((contact) => (
                <div key={contact.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-royal-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                    {contact.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{contact.name}</p>
                    <p className="text-sm text-slate-500 truncate">{contact.company || contact.email}</p>
                  </div>
                  <ChevronRight size={16} className="text-slate-400" />
                </div>
              ))}
              {recentContacts.length === 0 && (
                <p className="text-center text-slate-500 py-4">No contacts yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-cabinet text-lg">Open Tasks</CardTitle>
            <CardDescription>Tasks that need your attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {openTasks.slice(0, 5).map((task) => (
                <div key={task.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className={`w-3 h-3 rounded-full ${
                    task.priority === 'urgent' ? 'bg-red-500' :
                    task.priority === 'high' ? 'bg-orange-500' :
                    task.priority === 'medium' ? 'bg-gold-500' : 'bg-green-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{task.title}</p>
                    <p className="text-sm text-slate-500">{task.status.replace('_', ' ')}</p>
                  </div>
                  <Badge className={`priority-${task.priority}`}>{task.priority}</Badge>
                </div>
              ))}
              {openTasks.length === 0 && (
                <p className="text-center text-slate-500 py-4">No open tasks</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

